import { createSelector } from 'reselect';
import get from 'lodash/get';

export const selectDataConstants = state => state.data.constants;
export const reqPrivacySelector = state => state.data.privacyPolicies;
export const selectProcessedConsts = state => state.data.processedConsts;
export const selectDataConfigurations = state => state.data.configurations;

export const selectDataConfigurationsList = createSelector(
  selectDataConfigurations,
  request => get(request, 'list', []),
);

export const selectDataConfigurationsLoading = createSelector(
  selectDataConfigurations,
  request => get(request, 'sending', false),
);

export const privacyListSelector = createSelector(
  reqPrivacySelector,
  request => get(request, 'list', []),
);

export const sendingPrivacySelector = createSelector(
  reqPrivacySelector,
  request => get(request, 'sending', false),
);

export const privacyCountriesSelector = createSelector(
  privacyListSelector,
  list => list.map(item => get(item, 'country')),
);

export const selectHealthInterestsList = createSelector(
  selectProcessedConsts,
  request => get(request, 'healthInterests', []),
);

export const selectNameSuffixesList = createSelector(
  selectProcessedConsts,
  request => get(request, 'nameSuffixes', []),
);

export const selectVeteranStatusesList = createSelector(
  selectDataConstants,
  request => get(request, 'veteranStatuses', []),
);

export const selectEducationLevelsList = createSelector(
  selectDataConstants,
  request => get(request, 'educationLevels', []),
);

export const selectCitizinship = createSelector(
  selectDataConstants,
  request => get(request, 'citizenship', []),
);

export const selectMaritalStatus = createSelector(
  selectProcessedConsts,
  request => get(request, 'maritalStatus'),
);

export const selectGenderTypes = createSelector(
  selectProcessedConsts,
  request => get(request, 'genderTypes', []),
);

export const selectGenderIdentityList = createSelector(
  selectDataConstants,
  request => get(request, 'genderIdentities', []),
);

export const selectPlatformVersion = createSelector(
  selectDataConstants,
  request => get(request, 'version', '-'),
);

export const selectSsnHidingEnabled = createSelector(
  selectDataConstants,
  request => get(request, 'ssnHidingEnabled', false),
);

export const selectLanguageTypes = createSelector(
  selectProcessedConsts,
  request => get(request, 'languageTypes'),
);

export const selectRaceTypes = createSelector(
  selectProcessedConsts,
  request => get(request, 'raceCodes', []),
);

export const selectEthnicTypes = createSelector(
  selectProcessedConsts,
  request => get(request, 'ethnicTypes', []),
);

export const selectMedicationAllergies = createSelector(
  selectProcessedConsts,
  state => get(state, 'medicationAllergies', []),
);

export const selectHealthConditions = createSelector(
  selectProcessedConsts,
  state => get(state, 'healthConditions', []),
);

export const selectComplaintReasons = createSelector(
  selectDataConstants,
  constants => get(constants, 'complaintReasons', []),
);

export const selectRelationList = createSelector(
  selectDataConstants,
  constants => get(constants, 'relations', []),
);

export const selectFamilyMemberRoleMinorRoles = createSelector(
  selectDataConstants,
  constants => get(constants, 'familyMemberRoleMinorRoles', []),
);

export const selectInsuranceRelationList = createSelector(
  selectDataConstants,
  constants => get(constants, 'insuranceRelations', []),
);

export const selectPaymentDelaySeconds = createSelector(
  selectDataConstants,
  constants => get(constants, 'paymentsVerificationJobDelaySeconds', 240),
);

export const selectUsStates = createSelector(
  selectProcessedConsts,
  request => get(request, 'constantsStatesUs', []),
);

export const selectAppState = state => get(state, 'data.appState');

export const selectPendingDependentAssignmentsCounter = createSelector(
  selectAppState,
  state => get(state, 'providerPendingEventsFeedItems.totalFeedsCount', 0),
);

export const selectLoggedUserHasRelations = createSelector(
  selectAppState,
  state => get(state, 'caregiverOrGuardianOrProxyOrFamilyMember', false),
);

export const selectVisitTestCallPerformed = createSelector(
  selectAppState,
  state => get(state, 'virtualVisitNetworkTestPerformed', false),
);

export const selectAppointmentSchedulingAllowed = createSelector(
  selectAppState,
  state => get(state, 'appointmentSchedulingAllowed', false),
);

export const selectAppointmentSchedulingForSelfAllowed = createSelector(
  selectAppState,
  state => get(state, 'appointmentSchedulingForSelfAllowed', false),
);

export const selectAppCustomButtons = createSelector(
  selectAppState,
  state => get(state, 'customButtons', []),
);

export const selectAppRequestEmergencyContactInfo = createSelector(
  selectAppState,
  state => get(state, 'requestEmergencyContactInfo', true),
);

export const selectTimeZoneOptions = createSelector(
  selectDataConstants,
  state => {
    const timeZones = get(state, 'timeZones', []);
    return timeZones.map(({ name }) => ({
      label: name,
      value: name,
    }));
  },
);

const selectDataState = state => get(state, 'data');

/**
 *  COUNTRIES STATE
 * */
export const selectCountriesState = createSelector(
  selectDataState,
  state => get(state, 'countries'),
);
export const selectCountryList = createSelector(
  selectCountriesState,
  state => get(state, 'list', []),
);

export const selectCountryPhoneSettings = createSelector(
  selectCountryList,
  list => list.map(({ phone }) => phone),
);

export const selectCountryOptions = createSelector(
  selectCountryList,
  list => list.map(({ name, isoCode }) => ({ value: isoCode, label: name })),
);

/**
 *  COUNTRY DETAILS
 * */
export const selectCountryState = createSelector(
  selectDataState,
  state => get(state, 'countryDetails'),
);
export const selectCountryDetails = createSelector(
  selectCountryState,
  state => get(state, 'data', {}),
);

export const selectCountryDetailsLegalDocuments = createSelector(
  selectCountryState,
  state => get(state, 'data.legalDocuments', {}),
);

/**
 *  optAutCodes
 * */
export const selectOptAuthCodesState = createSelector(
  selectDataState,
  state => get(state, 'optAuthCodes'),
);

/**
 *  publicConstants
 * */
export const selectOtpCodeExpirationSeconds = createSelector(
  selectDataState,
  state => get(state, 'publicConstants.otpCodeExpirationSeconds', 0),
);

export const selectIsOtpEnabled = createSelector(
  selectDataState,
  state => get(state, 'publicConstants.otpEnabled', undefined),
);

export const selectPrimaryContactsEnabled = createSelector(
  selectDataState,
  state => get(state, 'publicConstants.primaryContactsEnabled', undefined),
);

export const selectPublicConstantsSending = createSelector(
  selectDataState,
  state => get(state, 'publicConstants.sending', false),
);



// WEBPACK FOOTER //
// ./src/redux/data/selectors.js